import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FormikProps, Formik, Form, Field } from "formik";
import { Col, Row } from "react-bootstrap";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { capitalize, map, pickBy } from "lodash";
import { InputCheckboxField } from "../../../../components/Shared/InputCheckboxField";
import * as XLSX from "xlsx";
import qs from "qs";
import { request } from "../../../../utils/Request";
import { getOrderStatus } from "../../../../components/Common/Display/OrderStatus";
import { getLocation } from "../../../../components/Common/Display/Location";
import { utcDateToLocalDate } from "../../../../utils/Helper";

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    dropdowns: any;
    visibility: any;
    toastify: any;
    onClose?: any;
}

type TInputForm = {
    consider_all_pages: boolean,
    brand: boolean,
    client: boolean,
    provider: boolean,
    brand_model: boolean,
    vin_number: boolean,
    order_cd: boolean,
    status: boolean,
    delivery_request_date: boolean,
    from_location: boolean,
    to_location: boolean,
    availability_date: boolean,
    height: boolean,
    width: boolean,
    length: boolean,
    net_weight: boolean,
    priority: boolean,
};

const DEFAULT_FORM_STATE = {
    consider_all_pages: true,
    brand: true,
    client: true,
    provider: true,
    brand_model: true,
    vin_number: true,
    order_cd: true,
    status: true,
    delivery_request_date: true,
    from_location: true,
    to_location: true,
    availability_date: true,
    height: true,
    length: true,
    width: true,
    net_weight: true,
    priority: true,
};

function OrderTransportRequestExcel(props: IFormProps) {
    const { t, visibility } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);

    const getTitles = () => ({
        brand: t('brand'),
        client: t('client'),
        provider: t('provider'),
        brand_model: t('model'),
        vin_number: t('vin number'),
        order_cd: t('order'),
        status: t('status'),
        delivery_request_date: t('delivery request date'),
        from_location: t('from location'),
        to_location: t('to location'),
        availability_date: t('created at'),
        height: t('height'),
        width: t('width'),
        length: t('length'),
        net_weight: t('net weight'),
        priority: t('priority'),
    });

    const onSubmit = async (payloads: TInputForm): Promise<void> => {
        const _params: any = { ...props.moduleState };
        const _payloads: any = payloads;

        if (payloads.consider_all_pages) {
            delete _params.page;
            delete _params.per_page;
        }

        try {
            const response = await request({
                url: `/suppliers/orders/transport/requests`,
                params: _params,
                paramsSerializer: {
                    serialize: (params: any) => qs.stringify(params)
                },
            });

            delete _payloads.consider_all_pages;
            const columns = pickBy(_payloads, (item: boolean) => (item));
            const title: any = getTitles();
            const data: any = [];

            map(response.data.data, (value: any) => {
                const row: any = {
                    brand: `${capitalize(value.brand.title)} - ${value.brand.alternate_title ?? ''}`,
                    provider: value.provider?.title || value.source_type || '',
                    brand_model: `${capitalize(value.brand_model.title)} - ${value.brand_model.version_code ?? ''}`,
                    vin_number: `${value.vin_number}`,
                    order_cd: `${value.order_cd}`,
                    status: getOrderStatus(value.status),
                    delivery_request_date: (
                        value?.source === 'alp_outbound_load_order'
                        && ( value?.alp_created_at || value?.load_created_at )
                    )
                        ? utcDateToLocalDate(value.alp_created_at || value.load_created_at)
                        : '',
                    from_location: getLocation(value.current_journey?.from_location),
                    to_location: getLocation(value.current_journey?.to_location),
                    availability_date: utcDateToLocalDate(value.created_at),
                    height: value.height,
                    client: value.client?.name,
                    width: value.width,
                    net_weight: value.net_weight,
                    length: value.length,
                    priority: (value.priorisation > 0 ? (value.built_to_order_cd > 0 ? 'SuperHigh' : 'High') : (value.built_to_order_cd > 0 ? 'SuperHigh' : '')),
                };

                const _row: any = {};
                map(columns, (_: any, key: string) => (_row[key] = row[key]));
                const arrayOfRow = Object.keys(_row).map((key) => _row[key]);
                data.push(arrayOfRow);
            });

            const _title = map(columns, (_: any, key: string) => (title[key] = title[key]));
            const excel = XLSX.utils.book_new();
            const sheet = XLSX.utils.aoa_to_sheet([_title, ...data]);
            // Keep same UX as Orders module
            XLSX.utils.book_append_sheet(excel, sheet, 'Orders');
            XLSX.writeFile(excel, "orders.xlsx");
        } catch (e: any) {
            props.toastify(t("server error"), "error");
        }
    };

    const onReset = () => {
        const base: any = formRef.current;
        if (base) {
            base.resetForm();
            setInitFormState({
                ...DEFAULT_FORM_STATE
            });
        }
    };

    const onClose = () => {
        setPanelState(false);
        if (props.onClose) props.onClose();
    };

    useEffect(() => {
        if (visibility) {
            setPanelState(visibility);
            setInitFormState({ ...DEFAULT_FORM_STATE });
        }
    }, [visibility]);

    return (
        <Sidebar
            header={
                <div className="bg-body-tertiary card-header">
                    <h5 className={"card-title mt-0"}>{t("export")} {t("orders")}</h5>
                    <p>{t("select columns to be exported.")}</p>
                </div>
            }
            visible={panelState} position="right" onHide={onClose} blockScroll={true}
            style={{ width: '650px' }} closeOnEscape={false}
        >
            <div style={{
                height: '3px',
                background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
            }}></div>

            <Formik
                innerRef={formRef}
                enableReinitialize={true}
                initialValues={initFormState}
                onSubmit={onSubmit}
            >
                {(formProps: FormikProps<TInputForm>) => (
                    <Form onSubmit={formProps.handleSubmit}>
                        <Row className={"mt-4"}>
                            <Col sm={12}>
                                <Field component={InputCheckboxField} name="consider_all_pages" label={t('consider all pages')} />
                            </Col>
                            {Object.keys(DEFAULT_FORM_STATE).filter(k => k !== 'consider_all_pages').map((key: string) => (
                                <Col sm={6} key={key} className={"mt-2"}>
                                    <Field component={InputCheckboxField} name={key} label={t(key.replaceAll('_', ' '))} />
                                </Col>
                            ))}
                        </Row>

                        <div className="filter filter-card-footer" style={{ width: '635px' }}>
                            <Button type={"submit"} disabled={formProps.isSubmitting} size={"small"} outlined>
                                <i className="pi pi-file-excel me-2" />
                                {t("export")}
                            </Button>
                            <Button type={"button"} disabled={formProps.isSubmitting} onClick={onReset}
                                    className={"ms-2"} size={"small"} severity={"warning"} outlined>
                                <i className="pi pi-refresh me-2" />
                                {t("reset")}
                            </Button>
                            <Button type={"button"} disabled={formProps.isSubmitting} onClick={onClose}
                                    className={"ms-2"} size={"small"} severity={"danger"} outlined>
                                <i className="pi pi-arrow-right me-2" />
                                {t("close")}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </Sidebar>
    );
}

OrderTransportRequestExcel.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(OrderTransportRequestExcel);

